/**
 * GroupDetailView — Full group context with leaderboard.
 * Data flows through useWorldCupGroupDetailData adapter hook.
 * Group lists are small (<100 members), so rest rows use map() inside the
 * parent ScrollView rather than a nested FlashList.
 */

import React, { useMemo, useCallback, useRef, useState, useEffect } from 'react';
import { View, Text, Image, Pressable, Platform, Dimensions, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { FlashListRef } from '@shopify/flash-list';
import { ChevronLeft, Lock, WifiOff } from 'lucide-react-native';
import { ARENA_EMPTY_MONKEY_SOURCE } from '../../../constants/assets';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useWorldCupGroupDetailData } from '../../../src/worldcup/hooks/useWorldCupGroupDetailData';
import { worldCupGroupLeaderboardInfiniteQueryOptions } from '../../../src/worldcup/hooks/useWorldCupGroupLeaderboardQuery';
import { SQUAD_LEADERBOARD_PAGE_SIZE } from '../../../src/worldcup/helpers/squadLeaderboardPagination.helper';
import { LeaderboardLoadMore } from '../WorldCupLeaderboard/LeaderboardLoadMore';
import { WORLD_CUP_PREFETCH_DELAY_MS } from '../../../src/worldcup/constants/worldcupReactQuery.constants';
import { useWorldCupMatchesQuery } from '../../../src/worldcup/hooks/useWorldCupMatchesQuery';
import { useWorldCupGroupMembershipMutations } from '../../../src/worldcup/hooks/useWorldCupGroupMembershipMutations';
import { useCompetitionId, useCompetitionBasePath, useCompetitionSurface } from '../../../src/competitions/contexts/CompetitionContext';
import { competitionGroupsRoute, competitionMemberProfileRoute } from '../../../src/competitions/helpers/competitionRoutes.helper';
import { worldCupQueryKeys } from '../../../src/worldcup/constants/worldcupQueryKeys';
import {
  WORLD_CUP_GROUPS_TAB,
} from '../../../src/worldcup/constants/worldcupTabRoutes.constants';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useAddFriendFromMenu } from '../../../src/friends/hooks/useAddFriendFromMenu';
import { useFriendsData } from '../../../contexts/FriendsDataContext';
import { buildFriendStatusByUserId } from '../../../src/friends/helpers/friendStatusMap.helper';
import { useSkeletonLoading } from '../../../src/shared/hooks/useSkeletonLoading';
import { useSnackbar } from '../../../src/shared/hooks/useSnackbar';
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper';
import { sanitizeInput } from '../../../src/helpers/sanitizeInput.helper';
import { patchWorldCupCompetitionGroup } from '../../../src/worldcup/helpers/worldCupCompetitionGroupsApi';
import { FadeInContent } from '../../shared/FadeInContent';
import { WorldCupSkeleton } from '../WorldCupSkeleton/WorldCupSkeleton';
import { WorldCupLeaderboard } from '../WorldCupLeaderboard/WorldCupLeaderboard';
import type { LeaderboardPeriod } from '../WorldCupLeaderboard/PodiumStage.constants';
import { GroupHeroBanner } from '../GroupHeroBanner/GroupHeroBanner';
import { GroupInfoSheet } from '../GroupInfoSheet/GroupInfoSheet';
import { GroupMemberRowMenu } from './GroupMemberRowMenu';
import { SquadLeaderboardSearch } from './SquadLeaderboardSearch';
import { LeaderboardSelfStandingBar } from '../WorldCupLeaderboard/LeaderboardSelfStandingBar';
import { SquadTvModeToggle } from '../SquadTvModeToggle/SquadTvModeToggle';
import { SquadTvDisplay } from '../SquadTvDisplay';
import { useSquadTvModeStore } from '../../../src/worldcup/stores/squadTvModeStore';
import { useSquadTvLeaderboardQuery } from '../../../src/worldcup/hooks/useSquadTvLeaderboardQuery';
import { getGroupDetailViewStyles } from './GroupDetailView.styles';
import {
  GROUP_DETAIL_COPY,
  ACCOUNTS_USER_QUERY_KEY,
} from './GroupDetailView.constants';
import type { LeaderboardEntry } from '../../../src/worldcup/types/worldcup.types';

interface GroupDetailViewProps {
  groupId: string;
}

export const GroupDetailView: React.FC<GroupDetailViewProps> = ({ groupId }) => {
  const theme = useTheme();
  const { themeMode } = useThemeContext();
  const router = useRouter();
  const competitionId = useCompetitionId();
  const basePath = useCompetitionBasePath();
  /**
   * Flags on the World Cup surface, kits everywhere else (AC-3.6). The squad board draws the
   * same winner chip the global board does, so it needs the same test: without it a Champions
   * League row drew Barcelona's *country* flag, claiming the club is Spain.
   */
  const teamVisual = useCompetitionSurface() === 'worldcup' ? 'flag' : 'kit';
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const { width: windowWidth } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const { isSignedIn, isLoaded, userId } = useSupabaseAuth();
  const matchesQuery = useWorldCupMatchesQuery(competitionId, isLoaded && isSignedIn);
  const futuresOptions = matchesQuery.data?.futuresOptions ?? null;

  const { friends } = useFriendsData();
  const { handleAddFriend: addFriendFromMenu, sendingUserId: friendRequestSendingUserId } =
    useAddFriendFromMenu();
  const friendStatusByUserId = useMemo(
    () => buildFriendStatusByUserId(friends),
    [friends],
  );

  const [period, setPeriod] = useState<LeaderboardPeriod>('overall');
  const isTvModeActive = useSquadTvModeStore((s) => s.isActive);
  const tvModeGroupId = useSquadTvModeStore((s) => s.groupId);
  const enterTvMode = useSquadTvModeStore((s) => s.enterTvMode);
  const exitTvMode = useSquadTvModeStore((s) => s.exitTvMode);
  const isTvModeForGroup = isTvModeActive && tvModeGroupId === groupId;
  const {
    group,
    top3,
    rest,
    pinnedCurrentUser,
    currentUserEntry,
    isCurrentUserOnPage,
    currentUserRole,
    currentUserMembershipId,
    isLoading,
    isError,
    isLeaderboardSectionLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useWorldCupGroupDetailData(groupId, period);

  const tvLeaderboardQuery = useSquadTvLeaderboardQuery(competitionId, groupId, {
    enabled: isTvModeForGroup && isLoaded && isSignedIn,
    period: 'overall',
  });

  const flashListRef = useRef<FlashListRef<LeaderboardEntry> | null>(null);

  /**
   * The member has a standing bar whenever they have a row (Story 9, AC-48) —
   * persistent, mirroring the global leaderboard. It never renders an in-list
   * pinned footer, so the same member is never listed twice. Drives the reserved
   * bottom padding, kept constant regardless of the bar's live visibility so
   * fading it out never shifts the list.
   */
  const hasSelfBar =
    !isTvModeForGroup &&
    isSignedIn &&
    currentUserEntry !== null &&
    currentUserMembershipId !== null;

  /**
   * Whether the viewer's own row is currently on screen. When it is, we fade the
   * floating bar out so they don't see themselves twice — the same viewport-based
   * rule as the global leaderboard (AC-48). Off-page viewers never have a row in
   * the loaded list, so this stays false and the bar stays up.
   */
  const [isSelfRowOnScreen, setIsSelfRowOnScreen] = useState(false);

  const styles = useMemo(
    () =>
      getGroupDetailViewStyles(
        theme,
        themeMode,
        windowWidth,
        insets.bottom,
        hasSelfBar,
        isTvModeForGroup,
      ),
    [theme, themeMode, windowWidth, insets.bottom, hasSelfBar, isTvModeForGroup],
  );

  useEffect(() => {
    return () => {
      exitTvMode();
    };
  }, [exitTvMode, groupId]);

  const handleToggleTvMode = useCallback(() => {
    if (isTvModeForGroup) {
      exitTvMode();
      return;
    }
    enterTvMode(groupId);
  }, [enterTvMode, exitTvMode, groupId, isTvModeForGroup]);

  /**
   * Speculatively prefetch the inactive period so the Week/Overall toggle hits a
   * hot cache. Fires after a short delay so it never competes with the initial
   * Overall paint on group entry.
   */
  useEffect(() => {
    if (!isLoaded || !isSignedIn || groupId.length === 0) return;
    const otherPeriod: LeaderboardPeriod = period === 'overall' ? 'week' : 'overall';
    const timeoutId = setTimeout(() => {
      void queryClient.prefetchInfiniteQuery(
        worldCupGroupLeaderboardInfiniteQueryOptions(
          apiClient,
          competitionId,
          groupId,
          SQUAD_LEADERBOARD_PAGE_SIZE,
          otherPeriod,
        ),
      );
    }, WORLD_CUP_PREFETCH_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [apiClient, queryClient, groupId, isLoaded, isSignedIn, period]);

  const renameSquadMutation = useMutation({
    mutationFn: (nextName: string) => patchWorldCupCompetitionGroup(apiClient, competitionId, groupId, { name: nextName }),
    onSuccess: async () => {
      showSnackbar(GROUP_DETAIL_COPY.SQUAD_NAME_SAVED, 'success');
      await queryClient.invalidateQueries({
        queryKey: worldCupQueryKeys.groupLeaderboardAll(competitionId, groupId),
      });
      await queryClient.invalidateQueries({
        queryKey: worldCupQueryKeys.groups(competitionId),
      });
      await queryClient.invalidateQueries({ queryKey: ACCOUNTS_USER_QUERY_KEY });
    },
    onError: (e: unknown) => {
      showSnackbar(extractErrorMessage(e, 'Could not save squad name'), 'error');
    },
  });

  const handleRenameSquad = useCallback(
    async (nextName: string) => {
      await renameSquadMutation.mutateAsync(nextName);
    },
    [renameSquadMutation],
  );

  const canRenameSquad = group?.viewerRole === 'ADMIN' && !group?.companyBranding;

  const { isLoadingWithDelay, contentOpacity } = useSkeletonLoading({
    isLoading,
    isContentReady: !isLoading && group !== null,
  });

  const { confirmAndLeaveSquad, confirmAndKickMember, isKicking } =
    useWorldCupGroupMembershipMutations({ groupId, currentUserMembershipId });

  const [infoSheetOpen, setInfoSheetOpen] = useState(false);
  const [menuOpenForEntryId, setMenuOpenForEntryId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const menuTriggerRefs = useRef<Record<string, View | null>>({});

  const registerMenuTriggerRef = useCallback((entryId: string, ref: View | null) => {
    menuTriggerRefs.current[entryId] = ref;
  }, []);

  const handleOpenRowMenu = useCallback((entry: LeaderboardEntry) => {
    const ref = menuTriggerRefs.current[entry.id];
    if (ref) {
      ref.measureInWindow((x, y, width, height) => {
        const { width: screenWidth } = Dimensions.get('window');
        setMenuPosition({ top: y + height, right: screenWidth - (x + width) });
        setMenuOpenForEntryId(entry.id);
      });
    } else {
      setMenuPosition({ top: 120, right: 16 });
      setMenuOpenForEntryId(entry.id);
    }
  }, []);

  const handleCloseRowMenu = useCallback(() => {
    setMenuOpenForEntryId(null);
    setMenuPosition(null);
  }, []);

  const handleRemoveMember = useCallback(
    (entry: LeaderboardEntry) => {
      if (!entry.membershipId) return;
      confirmAndKickMember({
        membershipId: entry.membershipId,
        memberName: entry.name,
      });
    },
    [confirmAndKickMember],
  );

  const handleAddFriend = useCallback(
    (entry: LeaderboardEntry) => addFriendFromMenu(entry, handleCloseRowMenu),
    [addFriendFromMenu, handleCloseRowMenu],
  );

  const handleLeaveSquad = useCallback(() => {
    confirmAndLeaveSquad({ squadName: group?.name });
  }, [confirmAndLeaveSquad, group?.name]);

  const handleBack = useCallback(() => {
    router.replace(competitionGroupsRoute(basePath) as never);
  }, [router]);

  const handleLeaderboardRowPress = useCallback(
    (entry: LeaderboardEntry) => {
      if (!entry.userId) return;
      router.push({
        pathname: competitionMemberProfileRoute(basePath) as never,
        params: {
          groupId,
          memberId: entry.userId,
          wcTab: WORLD_CUP_GROUPS_TAB,
          ...(entry.isCurrentUser ? { self: '1', rank: String(entry.rank) } : {}),
        },
      } as never);
    },
    [router, groupId],
  );

  /**
   * "Your standing" bar tap — same behaviour as the global leaderboard. On-page
   * viewers have a real row, so scroll to it; off-page viewers have no row in the
   * loaded list, so open their own profile (self mode) instead.
   */
  const handleSelfBarPress = useCallback(() => {
    if (!currentUserEntry) return;
    if (!isCurrentUserOnPage) {
      handleLeaderboardRowPress(currentUserEntry);
      return;
    }
    flashListRef.current?.scrollToItem({ item: currentUserEntry, animated: true, viewPosition: 0.35 });
  }, [currentUserEntry, isCurrentUserOnPage, handleLeaderboardRowPress]);

  const handleSearchChangeText = useCallback(
    (text: string) => setSearchQuery(sanitizeInput(text, true)),
    [],
  );

  const isAdmin = currentUserRole === 'ADMIN';
  const isSquadMember = currentUserRole !== null && currentUserMembershipId !== null;
  const hasSearchQuery = searchQuery.trim().length > 0;

  /**
   * When searching, collapse top-3 into the flat list so all members are searchable.
   * Without a query, keep the normal top3/rest split so the podium renders.
   */
  const { displayTop3, filteredRest } = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return { displayTop3: top3, filteredRest: rest };
    }
    const all = [...top3, ...rest];
    return {
      displayTop3: [] as LeaderboardEntry[],
      filteredRest: all.filter((entry) => entry.name.toLowerCase().includes(normalizedQuery)),
    };
  }, [searchQuery, top3, rest]);

  // Server withholds peers' picks until the first kickoff; show one explanatory
  // banner so the locked rows read as intentional rather than a loading glitch.
  const peerFuturesHidden = useMemo(
    () => [...top3, ...rest].some((entry) => entry.futuresHidden && !entry.isCurrentUser),
    [top3, rest],
  );

  const tvModeToggle = (
    <SquadTvModeToggle
      isTvModeActive={isTvModeForGroup}
      iconOnly
      onPress={handleToggleTvMode}
    />
  );

  const tvTop3 = tvLeaderboardQuery.isLoading ? top3 : tvLeaderboardQuery.top3;
  const tvRest = tvLeaderboardQuery.rest;

  const rowMenuEntry = useMemo<LeaderboardEntry | null>(() => {
    if (!menuOpenForEntryId) return null;
    const pool: LeaderboardEntry[] = [...displayTop3, ...filteredRest];
    if (pinnedCurrentUser) pool.push(pinnedCurrentUser);
    return pool.find((entry) => entry.id === menuOpenForEntryId) ?? null;
  }, [displayTop3, filteredRest, pinnedCurrentUser, menuOpenForEntryId]);

  // Whole-page skeleton only fires on the truly-initial load (no group meta yet).
  // On a Week ↔ Overall toggle, `keepPreviousData` keeps `group` populated so we
  // skip this branch and let the leaderboard render its own section skeleton.
  if (isLoadingWithDelay && group === null) {
    return <WorldCupSkeleton variant="groupDetail" />;
  }

  if (isError) {
    return (
      <View style={styles.container}>
        <Pressable
          style={({ pressed, hovered }) => [
            styles.backButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.backButtonPressed,
            hovered && Platform.OS === 'web' && styles.backButtonHover,
          ]}
          onPress={handleBack}
        >
          <ChevronLeft size={20} color={theme.primary} />
          <Text style={styles.backLabel}>{GROUP_DETAIL_COPY.BACK_LABEL}</Text>
        </Pressable>
        <View style={styles.errorContainer}>
          <View style={styles.errorIconWrap}>
            <WifiOff size={48} color={theme.gray[400]} />
          </View>
          <Text style={styles.errorTitle}>{GROUP_DETAIL_COPY.ERROR_TITLE}</Text>
          <Text style={styles.errorSubtitle}>{GROUP_DETAIL_COPY.ERROR_SUBTITLE}</Text>
          <Pressable style={styles.errorButton} onPress={handleBack}>
            <Text style={styles.errorButtonLabel}>{GROUP_DETAIL_COPY.ERROR_RETRY}</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  if (!group) {
    return (
      <View style={styles.container}>
        <Pressable
          style={({ pressed, hovered }) => [
            styles.backButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.backButtonPressed,
            hovered && Platform.OS === 'web' && styles.backButtonHover,
          ]}
          onPress={handleBack}
        >
          <ChevronLeft size={20} color={theme.primary} />
          <Text style={styles.backLabel}>{GROUP_DETAIL_COPY.BACK_LABEL}</Text>
        </Pressable>
        <View style={styles.emptyState}>
          <Image source={ARENA_EMPTY_MONKEY_SOURCE} style={styles.emptyMonkey} resizeMode="contain" />
          <Text style={styles.emptyTitle}>{GROUP_DETAIL_COPY.EMPTY_TITLE}</Text>
          <Text style={styles.emptySubtitle}>{GROUP_DETAIL_COPY.EMPTY_SUBTITLE}</Text>
        </View>
      </View>
    );
  }

  if (isTvModeForGroup) {
    return (
      <FadeInContent opacity={contentOpacity} style={styles.tvRoot}>
        <SquadTvDisplay
          group={group}
          top3={tvTop3}
          rest={tvRest}
          onExitTv={handleToggleTvMode}
          futuresOptions={futuresOptions}
          isLeaderboardLoading={tvLeaderboardQuery.isLoading}
          autoScrollEnabled={
            !tvLeaderboardQuery.isLoading &&
            !tvLeaderboardQuery.isError &&
            (tvTop3.length > 0 || tvRest.length > 0)
          }
        />
      </FadeInContent>
    );
  }

  return (
    <FadeInContent opacity={contentOpacity} style={styles.container}>
      <WorldCupLeaderboard
        leaderboardGlass
        top3={displayTop3}
        rest={filteredRest}
        /* Off-page members are surfaced by the floating self-bar (+ jump-to-me),
           not an in-list footer — avoids the duplicate row (Story 9, AC-48). */
        pinnedCurrentUser={undefined}
        contentContainerStyle={styles.scrollContent}
        /* Auto-load the next page near the end — replaces the old onScroll handler
           now that the virtualized list owns scrolling (Story 9, AC-46). */
        onEndReached={fetchNextPage}
        onEndReachedThreshold={0.6}
        flashListRef={flashListRef}
        onCurrentUserVisibilityChanged={setIsSelfRowOnScreen}
        futuresOptions={futuresOptions}
        teamVisual={teamVisual}
        onLeaderboardRowPress={isSignedIn ? handleLeaderboardRowPress : undefined}
        onOpenRowMenu={isSignedIn ? handleOpenRowMenu : undefined}
        registerMenuTriggerRef={isSignedIn ? registerMenuTriggerRef : undefined}
        showPeriodToggle
        period={period}
        onPeriodChange={setPeriod}
        isLeaderboardLoading={isLeaderboardSectionLoading}
        rewards={group.companyBranding?.rewards ?? null}
        /* Hero banner (back button, badge, monkey mascot, group name) scrolls with
           the list as its top header so the whole screen stays one virtualized list. */
        listTopContent={
          <GroupHeroBanner
            group={group}
            onBack={handleBack}
            onInfoPress={() => setInfoSheetOpen(true)}
            trailingAction={tvModeToggle}
          />
        }
        listBottomContent={
          hasSearchQuery ? null : (
            <LeaderboardLoadMore
              hasMore={hasNextPage}
              isLoading={isFetchingNextPage}
              onPress={fetchNextPage}
            />
          )
        }
        belowPodium={
          <>
            {peerFuturesHidden ? (
              <View style={styles.futuresHiddenBanner}>
                <Lock size={15} color={theme.gray[600]} />
                <Text style={styles.futuresHiddenBannerText}>
                  {GROUP_DETAIL_COPY.FUTURES_HIDDEN_BANNER}
                </Text>
              </View>
            ) : null}
            <SquadLeaderboardSearch
              value={searchQuery}
              onChangeText={handleSearchChangeText}
              placeholder={GROUP_DETAIL_COPY.SEARCH_PLACEHOLDER}
              accessibilityLabel={GROUP_DETAIL_COPY.SEARCH_ACCESSIBILITY_LABEL}
            />
            {hasSearchQuery && filteredRest.length === 0 ? (
              <View style={styles.searchEmptyState}>
                <Text style={styles.searchEmptyLabel}>{GROUP_DETAIL_COPY.SEARCH_EMPTY_LABEL}</Text>
              </View>
            ) : null}
          </>
        }
      />

      {hasSelfBar && currentUserEntry ? (
        <LeaderboardSelfStandingBar
          entry={currentUserEntry}
          onPress={handleSelfBarPress}
          visible={!isSelfRowOnScreen}
        />
      ) : null}

      <GroupInfoSheet
        visible={infoSheetOpen}
        group={group}
        isSquadMember={isSquadMember}
        canLeaveSquad={group.viewerCanLeaveSquad !== false}
        canRenameSquad={canRenameSquad}
        renameSquadBusy={renameSquadMutation.isPending}
        onRenameSquad={handleRenameSquad}
        leaderboardEntries={[...top3, ...rest]}
        onClose={() => setInfoSheetOpen(false)}
        onLeave={() => {
          setInfoSheetOpen(false);
          handleLeaveSquad();
        }}
      />

      {rowMenuEntry && menuPosition ? (
        <GroupMemberRowMenu
          entry={rowMenuEntry}
          position={menuPosition}
          currentUserId={userId ?? null}
          canRemove={isAdmin}
          friendStatus={
            rowMenuEntry.userId ? friendStatusByUserId[rowMenuEntry.userId] ?? null : null
          }
          isFriendRequestSending={
            rowMenuEntry.userId != null && friendRequestSendingUserId === rowMenuEntry.userId
          }
          isMemberRemovalSending={isKicking}
          theme={theme}
          onClose={handleCloseRowMenu}
          onAddFriend={handleAddFriend}
          onRemoveMember={handleRemoveMember}
        />
      ) : null}
    </FadeInContent>
  );
};
